/** Pure helpers for the direct-load settings fields (no components, so Fast Refresh stays safe). */

export const MIB = 1024 * 1024;

/**
 * A positive integer typed into a field: undefined when the field is blank
 * (use the server default), null when it holds anything else that is not a
 * positive integer within `max` (block submission rather than silently fall
 * back to the default, or round a count the user did not type). With a
 * `scale` the field is in a larger unit (MiB) and the scaled value is rounded
 * to a whole number of the base unit.
 */
export function parsePositive(
  value: string,
  { scale = 1, max = Infinity }: { scale?: number; max?: number } = {},
): number | null | undefined {
  if (!value.trim()) return undefined;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const scaled = scale === 1 ? n : Math.round(n * scale);
  if (!Number.isInteger(scaled)) return null;
  return scaled >= 1 && scaled <= max ? scaled : null;
}

/** The per-load settings as typed; blank means the server default. */
export interface LoadSettingsInput {
  chunkMib: string;
  parallelism: string;
  /**
   * A full load: rewrite ingest rows whose values already match and
   * re-derive their products too. Off, a reload only transforms the rows
   * that changed.
   */
  retransformUnchanged: boolean;
  /**
   * Experimental SQL mode: merge the whole file in the database and transform
   * only the rows that changed (every row with `retransformUnchanged`). A
   * link that does not qualify loads chunked.
   */
  sqlMode: boolean;
}

export const EMPTY_LOAD_SETTINGS: LoadSettingsInput = {
  chunkMib: '',
  parallelism: '',
  retransformUnchanged: false,
  sqlMode: false,
};

/**
 * The typed settings parsed for the request: undefined fields take the server
 * default; `invalid` when a field holds anything else that is not a positive
 * number in range, which blocks submission.
 */
export type ParsedLoadSettings =
  | { invalid: true }
  | {
      invalid: false;
      chunkBytes: number | undefined;
      parallelism: number | undefined;
      skipUnchangedRows: boolean;
      sqlMode: boolean;
    };

export function parseLoadSettings(
  input: LoadSettingsInput,
): ParsedLoadSettings {
  const chunkBytes = parsePositive(input.chunkMib, { scale: MIB });
  const parallelism = parsePositive(input.parallelism, { max: 64 });
  if (chunkBytes === null || parallelism === null) return { invalid: true };
  return {
    invalid: false,
    chunkBytes,
    parallelism,
    skipUnchangedRows: !input.retransformUnchanged,
    sqlMode: input.sqlMode,
  };
}
